// Script for re-using the marketplace header and footer in WebFlow.
// It's intended to be used as a "Code Embded", here is how the script is used in WebFlow:

/*
<script src="https://www.vendr.com/assets/external/ExternalMarketplace.elkZRZIh.js"></script>

<script>
  document.addEventListener("DOMContentLoaded", () => {
    ExternalMarketplace.init({
      footerNode: document.querySelector("footer"),
      headerNode: document.getElementById("embed-header"),
      styleSheetUrl:
        "https://www.vendr.com/assets/external/ExternalMarketplace.Dwifw7a9.css",
      segmentWriteKey: "GUz9AOqQpNEDLM0PpL1pg2uaiQ4Sl5sl",
      clerkPublishableKey: "pk_test_XXX",
      reforgeApiKey:
        "426-Production-P454-E967-FRONTEND-bc23132b-0bd0-4d70-838b-0789aaf19c48",
      platformAppUrl: "https://app.vendr.com",
      algolia: {
        appId: "VYSCARNLSB",
        defaultIndex: "prod_backoffice",
        searchKey: "20d8ebb03c9f93edf1641cd97e5d970b",
      },
      identityV2Route: "https://www.vendr.com/api/identity/v2",
      areNewLandingPageLinksEnabled: true,
    });

      const header = document.getElementById("external-marketplace-header");
      const pageWrapper = header.closest(".page-wrapper").parentElement;
      pageWrapper.insertBefore(header, pageWrapper.firstChild);
  });
</script>

<div id="embed-header" style="height:80px"></div>
*/

import "~/globals.css";
import "./routes/_marketplace/marketplace.css";

import * as ReactDOM from "react-dom/client";
import { CookieConsent } from "~/components/CookieConsent";
import { Navbar } from "~/components/DesignSystem/Navbar";
import { FooterSection } from "~/components/DesignSystem/sections/FooterSection";
import { External } from "~/components/External";
import { Footer } from "~/components/Footer";
import { Header } from "~/components/Header";
import { RuthCard } from "~/components/RuthCard";
import { SaasHeader } from "~/components/SaasHeader";
import { CookieConsentProvider } from "~/contexts/cookieConsentContext";
import { NavbarVisibilityProvider } from "~/contexts/navbarVisibilityContext";
import * as AuthSchema from "~/routes/_marketplace.api.identity.v2/schema";

const fixExternalUrl = (url: string): string => {
  return url
    .replace(
      /ExternalMarketplace\.[A-Za-z0-9_-]+\.js/,
      "ExternalMarketplace.js",
    )
    .replace(
      /ExternalMarketplace\.[A-Za-z0-9_-]+\.css/,
      "ExternalMarketplace.css",
    )
    .replace(
      /www\.vendr\.com/,
      window.location.host.includes("preview.vendr-dev.com")
        ? "www.preview.vendr-dev.com"
        : "www.vendr.com",
    );
};

const getIdentityRoute = (configuredRoute?: string): string => {
  if (!configuredRoute) {
    return "/api/identity/v2";
  }
  return fixExternalUrl(configuredRoute);
};

const lookupAuth = async (route?: string) => {
  const identityRoute = getIdentityRoute(route);

  try {
    const response = await fetch(identityRoute, {
      mode: "cors",
      credentials: "include",
    });

    if (!response.ok) {
      console.warn(
        "[ExternalMarketplace] Identity API returned non-ok status:",
        response.status,
      );
      return AuthSchema.defaultResponse;
    }

    const body = await response.json();
    const result = AuthSchema.responseDecoder.safeParse(body);

    if (!result.success) {
      console.warn(
        "[ExternalMarketplace] Identity API response validation failed:",
        result.error.errors,
      );
      return AuthSchema.defaultResponse;
    }

    return result.data;
  } catch {
    return AuthSchema.defaultResponse;
  }
};

const renderInShadowDOM = (args: {
  target: Element;
  react: React.ReactNode;
  styleSheetUrl: string;
  segmentWriteKey?: string;
  reforgeApiKey?: string;
  clerkPublishableKey?: string | null;
  auth: AuthSchema.Response;
  algolia?: {
    appId: string;
    defaultIndex: string;
    searchKey: string;
    productTierIndex: string;
  };
  stickyToTop?: boolean;
  position?: string;
  zIndex?: string;
  id?: string;
}) => {
  const container = document.createElement("div");

  if (args.id) {
    container.id = args.id;
  }

  if (args.stickyToTop) {
    container.style.position = "sticky";
    container.style.top = "0px";
  }

  if (args.position) {
    container.style.position = args.position;
  }

  if (args.zIndex) {
    container.style.zIndex = args.zIndex;
  }

  args.target.replaceWith(container);

  const shadowRoot = container.attachShadow({ mode: "open" });

  // Preload CSS before rendering to prevent FOUC (Flash of Unstyled Content).
  // We append the stylesheet to the shadow root and wait for it to load before
  // rendering React content. If the stylesheet fails to load, we still render
  // to avoid blocking the page entirely - the content will be unstyled but functional.
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = args.styleSheetUrl;

  const renderContent = () => {
    const reactRoot = ReactDOM.createRoot(shadowRoot);
    reactRoot.render(
      <External
        styleSheetUrl={args.styleSheetUrl}
        algolia={args.algolia}
        auth={args.auth}
        clerkPublishableKey={args.clerkPublishableKey}
        reforgeApiKey={args.reforgeApiKey}
        segmentWriteKey={args.segmentWriteKey}
      >
        <style>
          {`.radix-themes:where([data-is-root-theme='true']) {
            min-height: unset;
            background: none;
          }`}
        </style>

        {args.react}
      </External>,
    );
  };

  link.onload = () => {
    renderContent();
  };
  link.onerror = () => {
    console.warn(
      "[ExternalMarketplace] Failed to load stylesheet:",
      args.styleSheetUrl,
    );
    renderContent();
  };
  shadowRoot.appendChild(link);
};

export type InitArgs = {
  bannerNode?: Element;
  headerNode?: Element;
  footerNode?: Element;
  ruthCardNode?: Element;
  cookieConsentNode?: Element;
  styleSheetUrl: string;
  platformAppUrl: string;
  nimbusUrl: string;
  segmentWriteKey?: string;
  reforgeApiKey?: string;
  clerkPublishableKey?: string;
  algolia?: {
    appId: string;
    defaultIndex: string;
    searchKey: string;
    productTierIndex: string;
  };
  identityV2Route?: string;
  newLandingPageEnabled?: boolean;
};

const onMobileMenuClicked = () => {
  const banner = document.getElementById("external-marketplace-banner");

  if (banner) {
    const isHidden = banner.style.display === "none";
    banner.style.display = isHidden ? "block" : "none";
  }
};

export const init = async (args: InitArgs) => {
  const authResponse = await lookupAuth(args.identityV2Route);

  // Use explicit override if provided, otherwise use flag from identity API
  const newLandingPageEnabled =
    args.newLandingPageEnabled ?? authResponse.newLandingPageEnabled;

  const styleSheetUrl = fixExternalUrl(args.styleSheetUrl);
  const auth = authResponse;

  if (args.footerNode) {
    renderInShadowDOM({
      id: "external-marketplace-footer",
      target: args.footerNode,
      styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: null,
      auth,
      algolia: args.algolia,
      zIndex: "0",
      react: newLandingPageEnabled ? <FooterSection /> : <Footer />,
    });
  }

  if (args.bannerNode && !newLandingPageEnabled) {
    renderInShadowDOM({
      id: "external-marketplace-banner",
      target: args.bannerNode,
      styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: null,
      auth,
      algolia: args.algolia,
      zIndex: "0",
      react: <SaasHeader />,
    });
  }

  if (args.headerNode) {
    renderInShadowDOM({
      id: "external-marketplace-header",
      target: args.headerNode,
      styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      // Only pass Clerk key for Header atm as it'd create a new MP context for each component (Header, Footer, Banner, etc) resulting
      // in this error:  @clerk/clerk-react: You've added multiple <ClerkProvider> components in your React component tree. Wrap your components in a single <ClerkProvider>
      clerkPublishableKey: args.clerkPublishableKey,
      auth,
      algolia: args.algolia,
      stickyToTop: true,
      zIndex: "10",
      react: newLandingPageEnabled ? (
        <NavbarVisibilityProvider>
          <Navbar nimbusUrl={args.nimbusUrl} />
        </NavbarVisibilityProvider>
      ) : (
        <Header
          platformAppUrl={args.platformAppUrl}
          channelTargetOrigin={window.document.URL}
          onMobileMenuClicked={onMobileMenuClicked}
          nimbusUrl={args.nimbusUrl}
        />
      ),
    });
  }

  if (args.ruthCardNode) {
    renderInShadowDOM({
      id: "external-marketplace-ruth-card",
      target: args.ruthCardNode,
      styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: null,
      auth,
      algolia: args.algolia,
      react: <RuthCard nimbusUrl={args.nimbusUrl} />,
    });
  }

  if (args.cookieConsentNode) {
    renderInShadowDOM({
      id: "external-marketplace-cookie-consent",
      target: args.cookieConsentNode,
      styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: null,
      auth,
      algolia: args.algolia,
      position: "fixed",
      zIndex: "9999",
      react: (
        <CookieConsentProvider>
          <CookieConsent />
        </CookieConsentProvider>
      ),
    });
  }
};
